import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "@/components/ui/select";
import EvidenceUploader from "@/components/EvidenceUploader";
import SLABadge from "@/components/SLABadge";
import { STATUS_LABELS, STATUS_COLORS, LEVEL_LABELS } from "@/lib/constants";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, PlayCircle, Filter, Volume2, ShieldCheck, Shuffle, MapPin, ClipboardCheck, AlertOctagon, Camera } from "lucide-react";

const SCOPE_META = {
  pi: {
    title: "Police Station complaints",
    subtitleFn: (u) => `Complaints filed for your police station${u?.police_station ? ` (${u.police_station})` : ""}. SLA: 30 min at PS.`,
  },
  acp: {
    title: "Complaints escalated to ACP",
    subtitleFn: (u) => `Escalated from Police Station because 30 min SLA was breached (and PI did not mark on-site). Your SLA: 30 min.`,
  },
  dcp: {
    title: "Complaints escalated to DCP",
    subtitleFn: (u) => `Escalated from ACP because 30 min SLA was breached. Final level. Your SLA: 30 min.`,
  },
  additional_cp: {
    title: "All escalated complaints",
    subtitleFn: () => "Every complaint that has been escalated at least once, for oversight.",
  },
  control_room: {
    title: "All complaints — Police Control Room",
    subtitleFn: () => "See every complaint across Nagpur. You can reassign a complaint to a different station.",
  },
};

export default function OfficerComplaints({ scope }) {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [refreshKey, setRefreshKey] = useState(0);
  const meta = SCOPE_META[scope] || SCOPE_META.pi;

  useEffect(() => {
    const params = statusFilter !== "all" ? { status: statusFilter } : {};
    api.get("/complaints", { params })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, [statusFilter, refreshKey]);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="overline text-slate-500 mb-1">Officer console</div>
            <h1 className="font-display font-bold text-3xl tracking-tight">{meta.title}</h1>
            <p className="text-slate-600 mt-1 text-sm">{meta.subtitleFn(user)}</p>
          </div>
          <div className="flex items-center gap-3">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48" data-testid="status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="in_progress">In progress</SelectItem>
                <SelectItem value="escalated">Escalated</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {items === null && <div className="text-muted-foreground">Loading...</div>}
        {items?.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center">
              <div className="text-slate-800 font-display text-xl font-semibold">No complaints found</div>
              <p className="text-slate-600 mt-2 text-sm">You&apos;re all caught up.</p>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-4">
          {(items || []).map((c) => (
            <ComplaintRow key={c.id} c={c} scope={scope} onChanged={() => setRefreshKey((k) => k + 1)} />
          ))}
        </div>
      </div>
    </Layout>
  );
}

function ComplaintRow({ c, scope, onChanged }) {
  const isOpen = !["resolved", "rejected"].includes(c.status);
  const canUpdate = ["pi", "acp", "dcp", "additional_cp"].includes(scope) && isOpen;
  // "Attend" is only for PI on their active PS-level complaints, and only before it's attended/resolved/rejected.
  const canAttend = scope === "pi" && c.current_level === "ps" && !["attended", "resolved", "rejected"].includes(c.status);
  return (
    <Card className="hover:border-slate-400 transition-colors">
      <CardContent className="py-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="overline text-slate-500">{c.reference}</span>
              <Badge variant="outline" className={STATUS_COLORS[c.status] || ""} data-testid={`status-${c.id}`}>
                {STATUS_LABELS[c.status] || c.status}
              </Badge>
              <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-md bg-slate-900 text-white">
                At {LEVEL_LABELS[c.current_level] || c.current_level}
              </span>
              {c.escalation_paused && !["resolved", "rejected"].includes(c.status) && (
                <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-md bg-indigo-100 text-indigo-900 border border-indigo-300" data-testid={`paused-${c.id}`}>
                  SLA paused
                </span>
              )}
              {!c.escalation_paused && (
                <SLABadge
                  createdAt={c.created_at}
                  levelStartedAt={c.level_started_at}
                  currentLevel={c.current_level}
                  status={c.status}
                  escalated={c.escalated}
                />
              )}
              <span className="text-xs text-muted-foreground">{c.zone_name} · {c.police_station_name}</span>
            </div>
            <div className="mt-2 font-display font-semibold text-lg tracking-tight">{c.title}</div>
            <div className="text-sm text-slate-600 mt-1 flex items-start gap-1">
              <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-400" />
              <span>{c.address}</span>
            </div>
            {c.decibel_reading?.peak_db > 0 && (
              <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-900 text-white text-xs font-medium" data-testid={`db-badge-${c.id}`}>
                <Volume2 className="w-3.5 h-3.5" />
                Peak {c.decibel_reading.peak_db} dB · Avg {c.decibel_reading.avg_db} dB
              </div>
            )}
            <div className="text-xs text-muted-foreground mt-2">
              Filed {new Date(c.created_at).toLocaleString()} · By {c.is_anonymous ? "Anonymous" : c.citizen_name || "—"}
              {c.filed_via === "control_room" ? " · via Control Room" : ""}
            </div>
          </div>
          <div className="flex flex-col gap-2 shrink-0 w-full sm:w-auto">
            <Link to={`/complaint/${c.id}`} className="w-full sm:w-auto">
              <Button variant="outline" size="sm" className="w-full sm:w-auto" data-testid={`view-btn-${c.id}`}>View</Button>
            </Link>
            {canAttend && <AttendDialog c={c} onChanged={onChanged} />}
            {canUpdate && <StatusUpdateDialog c={c} onChanged={onChanged} />}
            {scope === "control_room" && isOpen && (
              <ReassignDialog c={c} onChanged={onChanged} />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AttendDialog({ c, onChanged }) {
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState("attended");
  const [remark, setRemark] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  const needPhoto = action === "attended";

  const submit = async () => {
    if (remark.trim().length < 3) return toast.error("Please add a short remark");
    if (needPhoto && photos.length === 0) {
      return toast.error("Snap at least one on-site photo before marking attended.");
    }
    setBusy(true);
    try {
      await api.patch(`/complaints/${c.id}/attend`, { action, remark, attend_photos: photos });
      toast.success(action === "fake" ? "Complaint rejected as fake" : "Marked attended · SLA paused");
      setOpen(false);
      setRemark("");
      setPhotos([]);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="secondary" className="gap-2 w-full sm:w-auto" data-testid={`attend-btn-${c.id}`}>
          <ClipboardCheck className="w-4 h-4" /> On-site action
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md sm:max-w-lg w-[95vw] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">On-site action</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-sm text-slate-700">Choose what happened when you reached the location.</div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Action</label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger data-testid="attend-action-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="attended" data-testid="attend-option-attended">1.1 · Complaint Attended (pause SLA)</SelectItem>
                <SelectItem value="fake" data-testid="attend-option-fake">1.2 · Fake Complaint (reject &amp; close)</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-xs text-muted-foreground">
              {action === "attended"
                ? "SLA timer will be paused. Complete resolution with proof afterwards."
                : "The complaint will be closed as fake with your remark. No further escalation."}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">PI remark</label>
            <Textarea rows={3} value={remark} onChange={(e) => setRemark(e.target.value)}
              placeholder={action === "fake"
                ? "Reason for marking this complaint as fake (mandatory)."
                : "Brief note about the on-site situation."}
              data-testid="attend-remark-input" />
          </div>
          {needPhoto && (
            <div className="space-y-2 rounded-md border border-emerald-300 bg-emerald-50 p-3">
              <div className="flex items-center gap-2 text-emerald-900 text-sm font-medium">
                <Camera className="w-4 h-4" /> On-site photo required
              </div>
              <div className="text-xs text-emerald-900/80">
                Snap at least one photo at the scene (or upload from gallery). Higher officers use this as proof of a genuine visit.
              </div>
              <EvidenceUploader
                value={photos}
                onChange={setPhotos}
                accept="image/jpeg,image/png,image/jpg,image/webp"
                label="Add on-site photo"
                testid="attend-photo"
              />
              {photos.length === 0 && (
                <div className="text-xs text-red-700" data-testid="attend-photo-required-msg">At least one on-site photo is required.</div>
              )}
            </div>
          )}
          {action === "fake" && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-900 flex items-start gap-2">
              <AlertOctagon className="w-4 h-4 shrink-0 mt-0.5" />
              This will permanently close the complaint. Do not use if you can still take action.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || (needPhoto && photos.length === 0)}
            variant={action === "fake" ? "destructive" : "default"}
            data-testid="submit-attend-btn"
          >
            {busy ? "Saving..." : action === "fake" ? "Reject complaint" : "Confirm on-site"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatusUpdateDialog({ c, onChanged }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(c.status === "resolved" ? "resolved" : "in_progress");
  const [notes, setNotes] = useState("");
  const [proofs, setProofs] = useState([]);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (status === "resolved" && proofs.length === 0) {
      toast.error("Please upload proof (JPG, PNG, JPEG or PDF) before marking as resolved.");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/complaints/${c.id}/status`, {
        status,
        note: status === "in_progress" ? notes : undefined,
        resolution_notes: status === "resolved" ? notes : undefined,
        resolution_proofs: status === "resolved" ? proofs : [],
      });
      toast.success(`Marked as ${status.replace("_", " ")}`);
      setOpen(false);
      setProofs([]);
      setNotes("");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="gap-2" data-testid={`update-btn-${c.id}`}>
          {status === "resolved" ? <CheckCircle2 className="w-4 h-4" /> : <PlayCircle className="w-4 h-4" />}
          Update status
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Update complaint status</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">Reference</div>
            <div className="font-medium">{c.reference} · {c.title}</div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">New status</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="new-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {status === "resolved" ? "Resolution notes" : "Note (optional)"}
            </label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder={status === "resolved" ? "Describe the action taken" : "Add a note"}
              data-testid="status-notes-input" />
          </div>
          {status === "resolved" && (
            <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3">
              <div className="flex items-center gap-2 text-amber-900 text-sm font-medium">
                <ShieldCheck className="w-4 h-4" />
                Proof required to close the complaint
              </div>
              <div className="text-xs text-amber-900/80">
                Upload at least one file (JPG, PNG, JPEG or PDF).
              </div>
              <EvidenceUploader
                value={proofs}
                onChange={setProofs}
                accept="image/jpeg,image/png,image/jpg,application/pdf"
                label="Upload proof (JPG / PNG / JPEG / PDF)"
                testid="proof"
              />
              {proofs.length === 0 && (
                <div className="text-xs text-red-700" data-testid="proof-required-msg">At least one proof file is required.</div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || (status === "resolved" && proofs.length === 0)} data-testid="submit-status-update-btn">
            {busy ? "Saving..." : "Save update"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReassignDialog({ c, onChanged }) {
  const [open, setOpen] = useState(false);
  const [zone, setZone] = useState(c.zone);
  const [ps, setPs] = useState(c.police_station);
  const [note, setNote] = useState("");
  const [zones, setZones] = useState([]);
  const [stations, setStations] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/zones").then((r) => setZones(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!zone) return;
    api.get("/police-stations", { params: { zone } })
      .then((r) => setStations(r.data)).catch(() => setStations([]));
  }, [zone]);

  const submit = async () => {
    if (!ps) return toast.error("Select a police station");
    setBusy(true);
    try {
      await api.patch(`/complaints/${c.id}/reassign`, { police_station: ps, note });
      toast.success("Reassigned");
      setOpen(false);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="gap-2" data-testid={`reassign-btn-${c.id}`}>
          <Shuffle className="w-4 h-4" /> Reassign
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">Reassign to a different police station</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="text-sm">{c.reference} · currently at {c.police_station_name}</div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Zone</label>
            <Select value={zone} onValueChange={(v) => { setZone(v); setPs(""); }}>
              <SelectTrigger data-testid="reassign-zone"><SelectValue /></SelectTrigger>
              <SelectContent>
                {zones.map((z) => <SelectItem key={z.key} value={z.key}>{z.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Police station</label>
            <Select value={ps} onValueChange={setPs}>
              <SelectTrigger data-testid="reassign-ps"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {stations.map((s) => <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Note (optional)</label>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} data-testid="reassign-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="submit-reassign-btn">
            {busy ? "Reassigning..." : "Confirm reassignment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
